import { useCallback, useEffect, useMemo } from "react";

import {
  Avatar,
  BreadCrumbs,
  Button,
  ButtonIconWithLabel,
  Crumb,
  Icon_ChevronLeft24,
  Icon_Download24,
  PlatformAPI,
  useManagedModal,
} from "@shapr3d/frontend-lib";

import noThumbnail from "../../assets/images/no-thumbnail.png";
import {
  defaultPrivateSpaceName,
  defaultProjectTitleName,
  defaultTeamSpaceName,
} from "../../config";
import useNavigation from "../../hooks/navigation";
import useAccount from "../../hooks/useAccount";
import type { ProjectData } from "../../hooks/useProject";
import { useTeamActiveSubscription } from "../../hooks/useTeamActiveSubscription";
import { useTeam } from "../../hooks/useTeams";
import { useTracker } from "../../hooks/useTracker";
import { useWindowSize, ViewportType } from "../../hooks/useWindowSize";
import { ProjectRoleUtils, SubscriptionPeriod, type TeamID } from "../../types";
import { handleFileDownload } from "../../utils/handleFileDownload";
import { formatDate } from "../../utils/helpers";
import { DeepLinkButton } from "../DeepLinkButton/DeepLinkButton";
import { PaywallModal } from "../Headers/components/ShareModal/components/PaywallModal";
import { UserName } from "../UserName/UserName";

import style from "./ProjectPageHeader.module.css";

export const HeaderBreadCrumbs = ({ teamID, spaceName }: { teamID: TeamID; spaceName: string }) => {
  const tracker = useTracker();

  const { teamData } = useTeam(teamID);
  const teamName = teamData?.name;
  const { viewportType } = useWindowSize();

  const navigateTo = `/teams/${teamID}/shared-with-me`;

  if (!teamData) {
    return null;
  }

  return (
    <BreadCrumbs>
      {viewportType === ViewportType.Desktop && (
        <Crumb
          to={navigateTo}
          icon={<Avatar size="small" text={teamName} />}
          onClick={() => {
            tracker.trackGoToTeam({
              activeTeamId: teamID,
              destination: "shared_with_me",
              location: "breadcrumbs",
            });
          }}
        >
          {teamName}
        </Crumb>
      )}

      <Crumb
        to={navigateTo}
        icon={viewportType !== ViewportType.Desktop && <Icon_ChevronLeft24 />}
        onClick={() => {
          tracker.trackGoToSpace({
            activeTeamId: teamID,
            destination: "shared_with_me",
            location: "breadcrumbs",
            role: teamData.role,
          });
        }}
      >
        {spaceName}
      </Crumb>
    </BreadCrumbs>
  );
};

const spaceTypeNameMap: Record<string, string> = {
  private: defaultPrivateSpaceName,
  team: defaultTeamSpaceName,
};

export const ProjectPageHeader = ({ projectData }: { projectData: ProjectData }) => {
  const { data: accountData } = useAccount();
  const tracker = useTracker();

  const shouldShowDownloadButton =
    projectData?.latestRevision.fileDownload?.url && projectData.project.projectId;
  const shouldShowDeeplinkButton =
    projectData.project.projectId &&
    PlatformAPI.getInstance().isShaprSupportedPlatform() &&
    ProjectRoleUtils.isAtLeast(projectData.userRole, "editor");
  const shouldShowButtons = shouldShowDownloadButton || shouldShowDeeplinkButton;

  useEffect(() => {
    if (shouldShowDeeplinkButton) {
      tracker.trackDeeplinkButtonShown({
        projectId: projectData.project.projectId,
      });
    }
  }, [shouldShowDeeplinkButton, tracker, projectData.project.projectId]);

  const { data: hasTeamSubscription, isLoading: isSubscriptionDataLoading } =
    useTeamActiveSubscription(projectData?.teamId);

  const userName = useMemo(() => {
    const user = projectData.users?.find(
      (user) => user.userId === projectData.latestRevision.uploadedByUserId
    );
    if (user && user.userId === accountData?.id) {
      return user.name ? `${user.name} (You)` : "You";
    }

    return user?.name;
  }, [projectData, accountData]);

  const spaceName =
    projectData.space?.spaceName ??
    spaceTypeNameMap[projectData.space?.spaceType ?? ""] ??
    "Shared with me";

  const ownerPaywallModal = useManagedModal();
  const nonOwnerPaywallModal = useManagedModal();
  const navigation = useNavigation();

  const paywallReturnURL = useMemo(() => {
    const currentUrl = new URL(window.location.href);
    return currentUrl.toString();
  }, []);

  const handleDownloadClick = useCallback(() => {
    tracker.trackDownloadFileClicked({
      projectTeamId: projectData.teamId,
      projectId: projectData.project.projectId,
    });
    if (hasTeamSubscription) {
      handleFileDownload(projectData?.latestRevision.fileDownload?.url);
      return;
    }
    const isOwner = projectData && ProjectRoleUtils.isAtLeast(projectData.userRole, "owner");
    if (isOwner) {
      ownerPaywallModal.open();
    } else {
      nonOwnerPaywallModal.open();
    }
    if (!projectData.project.projectId) return;
    tracker.trackPaywallModalShown({
      remoteProjectId: projectData.project.projectId,
      paywallReason: "download_shapr_file",
      hasSubscribeAction: isOwner,
    });
  }, [tracker, projectData, hasTeamSubscription, ownerPaywallModal, nonOwnerPaywallModal]);

  return (
    <>
      <div className={style.headerMobile}>
        <img
          src={
            projectData?.latestRevision.thumbnailDownloads?.light?.url
              ? projectData?.latestRevision.thumbnailDownloads?.light?.url
              : noThumbnail
          }
        />

        <div className={style.headerMobileData}>
          <div>
            <HeaderBreadCrumbs teamID={projectData.teamId} spaceName={spaceName} />
            <div className={style.headerTitle}>
              {projectData.project.name || defaultProjectTitleName}
            </div>

            <div className={style.headerSubTitle}>
              <span style={{ marginRight: "4px" }}>
                Last edited: {formatDate(projectData.latestRevision.lastModifiedAt)} ·
              </span>{" "}
              <UserName name={userName ?? "-"} />
            </div>
            {shouldShowButtons && (
              <div className={style.ctaBoxTablet}>
                {shouldShowDownloadButton && (
                  <Button
                    variant="grayscale"
                    onClick={handleDownloadClick}
                    disabled={isSubscriptionDataLoading}
                  >
                    <ButtonIconWithLabel icon={<Icon_Download24 />} label="Save as .shapr file" />
                  </Button>
                )}
                {shouldShowDeeplinkButton && <DeepLinkButton />}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={style.wrapper}>
        <div className={style.header}>
          <div style={{ minWidth: 0 }}>
            <div className={style.pageTitle}>
              <HeaderBreadCrumbs teamID={projectData.teamId} spaceName={spaceName} />
            </div>

            <div className={style.headerTitle}>
              {projectData.project.name || defaultProjectTitleName}
            </div>
            <div className={style.headerSubTitle}>
              <span style={{ marginRight: "4px" }}>
                Last edited: {formatDate(projectData.latestRevision.lastModifiedAt ?? new Date())} ·
              </span>{" "}
              <UserName name={userName ?? "-"} />
            </div>
            <div className={style.ctaBoxMobile}>
              {shouldShowButtons && (
                <>
                  {shouldShowDownloadButton && (
                    <Button
                      variant="grayscale"
                      onClick={handleDownloadClick}
                      disabled={isSubscriptionDataLoading}
                    >
                      <ButtonIconWithLabel icon={<Icon_Download24 />} label="Save as .shapr file" />
                    </Button>
                  )}
                  {shouldShowDeeplinkButton && <DeepLinkButton />}
                </>
              )}
            </div>
          </div>

          {shouldShowButtons && (
            <div className={style.buttons}>
              {shouldShowDownloadButton && (
                <Button
                  variant="grayscale"
                  onClick={handleDownloadClick}
                  disabled={isSubscriptionDataLoading}
                >
                  <ButtonIconWithLabel icon={<Icon_Download24 />} label="Save as .shapr file" />
                </Button>
              )}
              {shouldShowDeeplinkButton && <DeepLinkButton />}
            </div>
          )}
        </div>
      </div>

      <PaywallModal
        modalProps={ownerPaywallModal.props}
        title={"Subscribe to export Shapr3D"}
        description={"Get instant access to all features without limits."}
        showSubscriptionButton={true}
        onClose={() =>
          tracker.trackPaywallModalDismissed({
            remoteProjectId: projectData.project.projectId,
            paywallReason: "download_shapr_file",
          })
        }
        onClick={() => {
          navigation.goToCheckout({
            period: SubscriptionPeriod.Yearly,
            showPeriodPicker: true,
            utmSource: "download_shapr_file",
            returnToURL: paywallReturnURL,
          });
          tracker.trackPaywallSubscribeClicked({
            paywallReason: "download_shapr_file",
            remoteProjectId: projectData.project.projectId,
          });
        }}
      />
      <PaywallModal
        modalProps={nonOwnerPaywallModal.props}
        title={"Your current team plan doesn’t include this feature."}
        size="medium"
        description={
          "Contact your team owner to upgrade the subscription and gain access to all Pro features."
        }
        onClose={() =>
          tracker.trackPaywallModalDismissed({
            remoteProjectId: projectData.project.projectId,
            paywallReason: "download_shapr_file",
          })
        }
        showSubscriptionButton={false}
      />
    </>
  );
};
